/**
 * How a call-sheet send is reported to the dispatcher.
 *
 * ─── WHY THIS IS ITS OWN MODULE ──────────────────────────────────────────────
 *
 * It is pure, so the TONE is assertable. That matters more than it sounds:
 * frontend #421 fixed a refusal that rendered as an empty state by routing it
 * into the screen's existing toast — which is green with a checkmark — so
 * "You don't have permission to change pay profiles" shipped under a tick and
 * stayed there until #452. Its tests were correct, green, and asserted
 * PRESENCE; nothing asserted appearance, so nothing could have caught it.
 *
 * The same trap is open here and wider. A call sheet that reached two of three
 * hands is not a success, and rendering it green with a smaller number is how
 * a dispatcher comes to believe a crew was called out when one of them was
 * skipped. So the tone is part of the return value and is pinned per case.
 *
 * ─── THE API CONTRACT (fieldiq-api #440) ─────────────────────────────────────
 *
 *   200 everyone reached   { sentTo: [...], skipped: [],     totalCrew }
 *   200 partial            { sentTo: [...], skipped: [...],  totalCrew }
 *   400 everyone opted out { error, data: { sentTo: [], skipped: [...] } }
 *   500 every send threw   { error, data: { sentTo: [], skipped: [...] } }
 */

/**
 * `known_bounce` (api, known-bounce refusal PR): the address is on the email
 * provider's suppression list. The API still ATTEMPTED the send — the mailbox
 * may have been fixed — and reports the hand here instead of in `sentTo`, so
 * the dispatcher knows the provider is expected to drop it. It carries the
 * server's dated sentence in `message`.
 */
export type SkipReason = 'opted_out' | 'send_failed' | 'known_bounce';

export interface SkippedRecipient {
  name?: string;
  contact?: string;
  reason?: SkipReason | string;
  message?: string;
}

export type SendTone = 'success' | 'warning' | 'error';

export interface SendReport {
  tone: SendTone;
  msg: string;
  detail: string[];
}

/**
 * `noun` is how the send reads in a sentence — "emailed", "texted". `verb`
 * names the PREFERENCE that was turned off.
 *
 * They are kept SEPARATE even though the two surviving channels happen to
 * agree, because they answer different questions — what the send did, versus
 * which control silences it — and a channel gated on another channel's
 * preference makes them diverge. The removed WhatsApp route was exactly that
 * (`noun: 'sent via WhatsApp'`, `verb: 'texts'`), and collapsing them now
 * would have to be undone by whoever adds the next such channel.
 */
export interface SendWording {
  noun: string;
  verb: 'emails' | 'texts';
}

function describeSkip(s: SkippedRecipient, verb: string): string {
  // A name we do not have must not render as an empty bullet or as the word
  // "undefined" — fall back to the contact, and only then to a plain label.
  const who = (s.name ?? '').trim() || (s.contact ?? '').trim() || 'A crew member';
  if (s.reason === 'send_failed') return `${who} — ${verb === 'emails' ? 'email' : 'text'} failed to send`;
  // A known-bounced address must not read as an opt-out: one is the crew
  // member's choice, the other is a dead mailbox the dispatcher has to fix.
  if (s.reason === 'known_bounce') {
    const contact = (s.contact ?? '').trim();
    return `${who} — ${contact ? `${contact} ` : ''}is on the email provider's suppression list; attempted, but expected to be dropped. Correct the address.`;
  }
  return `${who} — turned off call sheet ${verb}`;
}

/** Render a 2xx response body's `data` object. */
export function describeCallSheetSend(data: any, wording: SendWording): SendReport {
  const sentTo: string[] = Array.isArray(data?.sentTo) ? data.sentTo : [];
  const skipped: SkippedRecipient[] = Array.isArray(data?.skipped) ? data.skipped : [];
  const total: number =
    typeof data?.totalCrew === 'number' ? data.totalCrew : sentTo.length + skipped.length;

  if (skipped.length === 0) {
    return {
      tone: 'success',
      msg: `Call sheet ${wording.noun} to ${sentTo.length} crew member${sentTo.length !== 1 ? 's' : ''}`,
      detail: [],
    };
  }

  // WARNING, NOT SUCCESS. This is the common case and the one most likely to
  // be misread — see the module header.
  return {
    tone: 'warning',
    msg: `Call sheet ${wording.noun} to ${sentTo.length} of ${total} — ${skipped.length} skipped`,
    detail: skipped.map(s => describeSkip(s, wording.verb)),
  };
}

/**
 * Render a rejected request.
 *
 * THE SERVER'S OWN MESSAGE WINS. The 400 says exactly why nobody was reached
 * and names them; replacing it with "Failed to text call sheet" throws away
 * the only thing the dispatcher needs and leaves a generic red banner over a
 * specific, actionable cause. The fallback is for a request that never got an
 * answer at all — a network drop, not a refusal.
 */
export function describeCallSheetFailure(
  err: any,
  fallback: string,
  wording: SendWording,
): SendReport {
  const body = err?.response?.data;
  const skipped: SkippedRecipient[] = Array.isArray(body?.data?.skipped) ? body.data.skipped : [];
  return {
    tone: 'error',
    msg: (typeof body?.error === 'string' && body.error.trim()) || fallback,
    detail: skipped.map(s => describeSkip(s, wording.verb)),
  };
}
